import { zodResolver } from '@hookform/resolvers/zod';
import { Button, TextInput } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { LayoutGroup, motion } from 'framer-motion';
import { Globe, MapPin } from 'lucide-react';
import {
	type FC,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';
import { useHostContext } from '@/context/HostContext.tsx';
import useQueryParams from '@/hooks/useQueryParams';
import IntroToggleButton from '@/pages/AgentIntro/components/IntroToggleButton';
import { partnerMetadata } from '@/services/PartnerMetadata';
import useAppStore from '@/store/app.ts';
import { cn } from '@/utils';
import { createTrainingSourceSelectorSchema } from './TrainingSourceSelector.schema';
import {
	AgentIntroSourceType,
	type TrainingSourceSelectorForm,
	type TrainingSourceSelectorProps
} from './TrainingSourceSelector.type';

const TrainingSourceSelector: FC<TrainingSourceSelectorProps> = ({
	handleSubmit,
	isPending
}) => {
	const { localization } = useHostContext();
	const isMobile = useMediaQuery('(max-width: 768px)');
	const {
		params: { google_map_url, source }
	} = useQueryParams();
	const { isMiniCreatorMode } = useHostContext();
	const isAcronisVariant = partnerMetadata.getIsAcronisVariant();
	const { agentQuery } = useAppStore(
		useShallow((state) => ({
			agentQuery: state.agentQuery
		}))
	);
	const inputRef = useRef<HTMLInputElement>(null);
	const placesLib = useMapsLibrary('places');
	const schema = useMemo(
		() =>
			createTrainingSourceSelectorSchema(
				localization.translate.bind(localization)
			),
		[localization]
	);
	const form = useForm<TrainingSourceSelectorForm>({
		resolver: zodResolver(schema),
		mode: 'onChange',
		defaultValues: {
			sourceType: google_map_url
				? AgentIntroSourceType.GoogleMap
				: AgentIntroSourceType.Website,
			websiteUrl: source ?? '',
			googleMapUrl: google_map_url ?? ''
		}
	});

	const sourceType = form.watch('sourceType');

	const googleMapRawError = form.formState.touchedFields.googleMapUrl
		? form.formState.errors.googleMapUrl?.message
		: undefined;
	const [googleMapDisplayedError, setGoogleMapDisplayedError] = useState<
		string | undefined
	>();

	useEffect(() => {
		if (!googleMapRawError) {
			setGoogleMapDisplayedError(undefined);
			return;
		}
		const t = setTimeout(
			() => setGoogleMapDisplayedError(googleMapRawError),
			300
		);
		return () => clearTimeout(t);
	}, [googleMapRawError]);

	const onSubmit = (data: TrainingSourceSelectorForm) => {
		const value =
			sourceType === AgentIntroSourceType.Website
				? data.websiteUrl
				: data.googleMapUrl;
		if (!value) {
			return;
		}

		handleSubmit(value, sourceType);
	};

	const handleChangeTab = useCallback(
		(tab: AgentIntroSourceType) => {
			form.setValue('sourceType', tab, {
				shouldValidate: false,
				shouldDirty: false
			});

			form.resetField(
				tab === AgentIntroSourceType.Website ? 'websiteUrl' : 'googleMapUrl',
				{
					keepDirty: false,
					keepTouched: false
				}
			);
		},
		[form]
	);

	useEffect(() => {
		if (
			!placesLib ||
			!inputRef.current ||
			sourceType !== AgentIntroSourceType.GoogleMap
		) {
			return;
		}

		const autocomplete = new placesLib.Autocomplete(inputRef.current, {
			fields: ['url', 'place_id']
		});

		const listener = autocomplete.addListener('place_changed', () => {
			const place = autocomplete.getPlace();
			const url =
				place.url ??
				(place.place_id
					? `https://www.google.com/maps/place/?q=place_id:${place.place_id}`
					: undefined);
			if (url) {
				form.setValue('googleMapUrl', url, {
					shouldValidate: true
				});
			}
		});

		return () => {
			listener.remove();
		};
	}, [sourceType, placesLib, form]);

	return (
		<form
			className={cn(
				'w-full flex gap-[32px] justify-between items-center p-6 rounded-2xl border border-gray-200 bg-white shadow-xl',
				{
					'flex-col': isMobile || isMiniCreatorMode || isAcronisVariant,
					'flex-wrap': isAcronisVariant
				}
			)}
			onSubmit={form.handleSubmit(onSubmit)}
		>
			<div
				className={cn('relative intro-toggle-container w-[65%]', {
					'w-full': isMobile || isMiniCreatorMode,
					'w-3/5': isAcronisVariant && !isMobile && !isMiniCreatorMode
				})}
			>
				<LayoutGroup>
					<motion.div
						animate={{
							x: sourceType === AgentIntroSourceType.Website ? 0 : '100%'
						}}
						className="absolute top-0 left-0 h-[36px] rounded-lg bg-white shadow-xl ring-1 ring-black/10"
						layoutId="intro-tabs-indicator"
						style={{
							width: '50%'
						}}
						transition={{
							type: 'spring',
							stiffness: 500,
							damping: 35,
							mass: 0.6
						}}
					/>

					<div className="relative z-10 flex gap-[16px] h-[36px]">
						<IntroToggleButton
							active={sourceType === AgentIntroSourceType.Website}
							icon={<Globe className="h-[15px] w-[15px]" />}
							label={localization.translate(
								'creator',
								'trainingSourceSelection.tabs.website'
							)}
							onClick={handleChangeTab}
							tab={AgentIntroSourceType.Website}
						/>

						<IntroToggleButton
							active={sourceType === AgentIntroSourceType.GoogleMap}
							icon={<MapPin className="h-[15px] w-[15px]" />}
							label={localization.translate(
								'creator',
								'trainingSourceSelection.tabs.googleMaps'
							)}
							onClick={handleChangeTab}
							tab={AgentIntroSourceType.GoogleMap}
						/>
					</div>
				</LayoutGroup>
			</div>
			<div className="w-full flex gap-[32px] justify-between items-center">
				{sourceType === AgentIntroSourceType.Website && (
					<Controller
						control={form.control}
						key={AgentIntroSourceType.Website}
						name="websiteUrl"
						render={({ field, fieldState }) => (
							<TextInput
								{...field}
								className="!shadow-none p-0 w-full"
								classNames={{
									input:
										'text-2xl sm:text-base px-2 font-semibold min-h-fit border-0 p-0 outline-none no-autofill !shadow-none training-source-input'
								}}
								data-testid="creator-training-source-website-input"
								error={
									fieldState.isTouched ? fieldState.error?.message : undefined
								}
								onChange={(e) => {
									field.onChange(e);
									form.clearErrors('googleMapUrl');
								}}
								p={0}
								placeholder={localization.translate(
									'creator',
									'trainingSourceSelection.inputs.websitePlaceholder'
								)}
								ref={inputRef}
							/>
						)}
					/>
				)}

				{sourceType === AgentIntroSourceType.GoogleMap && (
					<Controller
						control={form.control}
						key={AgentIntroSourceType.GoogleMap}
						name="googleMapUrl"
						render={({ field }) => (
							<TextInput
								{...field}
								className="!shadow-none w-full"
								classNames={{
									input:
										'text-[24px] flex-1 sm:text-base min-h-fit p-0 font-semibold border-0 outline-none no-autofill !shadow-none training-source-input'
								}}
								error={googleMapDisplayedError}
								onChange={(e) => {
									field.onChange(e);
									form.clearErrors('websiteUrl');
								}}
								p={0}
								placeholder={localization.translate(
									'creator',
									'trainingSourceSelection.inputs.googleMapsPlaceholder'
								)}
								ref={inputRef}
								value={undefined}
							/>
						)}
					/>
				)}

				<Button
					className="w-full h-[56px] md:max-w-fit text-[16px]"
					data-testid="creator-training-source-submit"
					disabled={!form.formState.isValid}
					loading={isPending}
					style={{
						borderRadius: `${partnerMetadata.getSubmitCtaRadius()}px`
					}}
					type="submit"
				>
					{agentQuery?.buttonLabel ||
						localization.translate(
							'creator',
							'trainingSourceSelection.cta.submit'
						)}
				</Button>
			</div>
		</form>
	);
};

export default TrainingSourceSelector;
